import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { ArrowRight, BookOpen, Play, Video } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';

import { EmbeddedVideoPlayer } from '@/components/embeddedVideoPlayer';
import { Box } from '@/components/ui/box';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { usePrimumAssets } from '@/hooks/usePrimumAssets';
import type { ContentCard } from '@/types/messageData';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import {
  trackContentSuggestionClicked,
  trackContentSuggestionVideoPlayed,
  trackContentSuggestionViewed,
} from '@/util/analyticsEvents';
import { useSizedImageUrl } from '@/util/imageWidths';

/** The card's `md:max-w-[420px]`, the widest its thumbnail is drawn. */
const BUBBLE_MAX_WIDTH = 420;

interface ContentSuggestionBubbleProps {
  contentCard: ContentCard;
  conversationId: string;
  messageSid: string;
}

/**
 * "While you wait" suggestion card for a `content_suggestion` message. Paints
 * entirely from the message's `contentCard` attributes (no fetch on the render
 * path). Rendered only for the submitter; the dispatch in `renderMessage`
 * enforces that.
 *
 * Two layouts share one set of card chrome:
 * - **Article** (default): the whole card is a single press target that
 *   navigates to the in-app Content Library item by id (behind the oncologist
 *   attestation gate).
 * - **Video**: when the card is a video post that carries `videoProvider` +
 *   `videoId`, the thumbnail becomes an inline player. It stays a lightweight
 *   poster until pressed, then mounts `EmbeddedVideoPlayer` so a thread with
 *   several cards does not spin up a WebView/iframe per card up front. The
 *   footer keeps a separate link out to the full item. If either video field
 *   is missing, the card falls back to the article layout — so nothing breaks
 *   before the backend populates the fields.
 *
 * `matchedExcerpt` (the submitter's own question) is not shown visually; it
 * feeds the accessibility hint so screen-reader users hear why the item was
 * suggested.
 */
export const ContentSuggestionBubble = memo(function ContentSuggestionBubble({
  contentCard,
  conversationId,
  messageSid,
}: ContentSuggestionBubbleProps) {
  const { navigate } = useRouter();

  const { getCloudFrontUrl, isCloudFrontAvailable } = usePrimumAssets();

  const {
    contentLibraryItemId,
    contentType,
    title,
    subtitle,
    thumbnailUrl,
    readTime,
    matchedTopicName,
    matchedExcerpt,
    eyebrow,
    videoProvider,
    videoId,
  } = contentCard;

  const [isPlaying, setIsPlaying] = useState(false);

  const isVideoCard = contentType?.toLowerCase() === 'video' && !!videoProvider && !!videoId;

  useEffect(() => {
    trackContentSuggestionViewed({ itemId: contentLibraryItemId, conversationId, messageSid });
  }, [contentLibraryItemId, conversationId, messageSid]);

  const handleOpenItem = () => {
    trackContentSuggestionClicked({ itemId: contentLibraryItemId, conversationId, messageSid });

    navigate(`/content-library/${contentLibraryItemId}`);
  };

  const showThumbnail = isCloudFrontAvailable && !!thumbnailUrl;

  const thumbnailSource = useSizedImageUrl(
    showThumbnail ? getCloudFrontUrl(thumbnailUrl) : undefined,
    BUBBLE_MAX_WIDTH
  );

  const eyebrowRow = (
    <VStack space="sm">
      <HStack className="items-center" space="xs">
        <Icon as={isVideoCard ? Video : BookOpen} size="xs" className="text-primary-700" />
        <Text className="font-semibold text-2xs uppercase tracking-wide text-primary-700">
          {eyebrow ?? 'While you wait'}
        </Text>
      </HStack>

      {matchedTopicName ? (
        <Text className="text-xs text-typography-700">Matched · {matchedTopicName}</Text>
      ) : null}
    </VStack>
  );

  const titleBlock = (
    <VStack space="xs">
      <Heading className="font-bold text-base text-typography-900" numberOfLines={2}>
        {title}
      </Heading>
      {subtitle ? (
        <Text className="text-sm text-typography-700" numberOfLines={2}>
          {subtitle}
        </Text>
      ) : null}
    </VStack>
  );

  const footer = (
    <HStack className="items-center justify-between border-t border-outline-100 pt-3">
      <Text className="text-2xs uppercase tracking-wide text-typography-600">
        Primum Content Library{readTime ? ` · ${readTime}` : ''}
      </Text>
      <HStack className="items-center" space="xs">
        <Text className="font-semibold text-sm text-primary-700">
          {isVideoCard ? 'View' : 'Read'}
        </Text>
        <Icon as={ArrowRight} size="xs" className="text-primary-700" />
      </HStack>
    </HStack>
  );

  if (isVideoCard) {
    const playA11y = getAccessibilityProps({
      label: `Play video: ${title}`,
      role: 'button',
      ...(matchedExcerpt ? { hint: `Suggested because you asked: ${matchedExcerpt}` } : {}),
    });

    const openA11y = getAccessibilityProps({
      label: `View ${title} in the Content Library`,
      role: 'link',
    });

    return (
      <View className="w-full max-w-full overflow-hidden rounded-lg border border-outline-200 bg-background-0 md:max-w-[420px]">
        <VStack className="p-4" space="sm">
          {eyebrowRow}

          <Box className="aspect-[16/9] w-full overflow-hidden rounded-md bg-black">
            {isPlaying ? (
              <EmbeddedVideoPlayer
                autoplay
                contentLibraryItemId={contentLibraryItemId}
                onFirstPlay={() =>
                  trackContentSuggestionVideoPlayed({
                    itemId: contentLibraryItemId,
                    conversationId,
                    messageSid,
                    videoProvider,
                    videoId,
                  })
                }
                videoId={videoId}
                videoProvider={videoProvider}
              />
            ) : (
              <Pressable
                onPress={() => setIsPlaying(true)}
                className="h-full w-full web:cursor-pointer"
                {...playA11y}>
                {showThumbnail ? (
                  <Image
                    alt={`${title} thumbnail`}
                    contentFit="cover"
                    source={thumbnailSource}
                    style={{ height: '100%', width: '100%' }}
                  />
                ) : null}
                <View className="absolute inset-0 items-center justify-center">
                  <View className="h-14 w-14 items-center justify-center rounded-full bg-black/50">
                    <Icon as={Play} size="xl" className="text-white" />
                  </View>
                </View>
              </Pressable>
            )}
          </Box>

          {titleBlock}

          <Pressable onPress={handleOpenItem} className="web:cursor-pointer" {...openA11y}>
            {footer}
          </Pressable>
        </VStack>
      </View>
    );
  }

  const a11yProps = getAccessibilityProps({
    label: `Suggested reading: ${title}`,
    role: 'button',
    ...(matchedExcerpt ? { hint: `Suggested because you asked: ${matchedExcerpt}` } : {}),
  });

  return (
    <Pressable
      onPress={handleOpenItem}
      className="w-full max-w-full overflow-hidden rounded-lg border border-outline-200 bg-background-0 web:cursor-pointer md:max-w-[420px]"
      {...a11yProps}>
      <VStack className="p-4" space="sm">
        {eyebrowRow}

        {showThumbnail ? (
          <Box className="aspect-[16/9] w-full overflow-hidden rounded-md">
            <Image
              alt={`${title} thumbnail`}
              contentFit="cover"
              source={thumbnailSource}
              style={{ height: '100%', width: '100%' }}
            />
          </Box>
        ) : null}

        {titleBlock}

        {footer}
      </VStack>
    </Pressable>
  );
});
